import { useState } from "react";
import type { InventoryAdjustment } from "@/types";
import { formatDate, formatNumber } from "@/utils/format";

interface InventoryAdjustmentHistoryProps {
  adjustments: InventoryAdjustment[];
}

// Historial de ajustes y traspasos de inventario: qué producto, cuánto, por qué
// y quién lo hizo. Es la constancia visible de los cambios manuales de stock.
const InventoryAdjustmentHistory = ({ adjustments }: InventoryAdjustmentHistoryProps) => {
  const [expanded, setExpanded] = useState(false);

  if (adjustments.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-4 md:px-5 py-8 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-history-line text-2xl" />
        </span>
        <p className="text-sm font-semibold text-foreground-700">Sin ajustes ni traspasos todavía</p>
        <p className="text-xs text-foreground-500 mt-1">
          Cuando ajustes stock o muevas unidades entre productos, verás aquí la constancia.
        </p>
      </div>
    );
  }

  const visible = expanded ? adjustments : adjustments.slice(0, 8);

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-background-300/30 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0">
            <i className="ri-history-line" />
          </span>
          <p className="text-sm font-bold text-foreground-950">
            Historial de ajustes y traspasos
          </p>
        </div>
        <span className="text-[11px] text-foreground-500 whitespace-nowrap">
          {formatNumber(adjustments.length)} registros
        </span>
      </div>

      <ul className="divide-y divide-background-300/25">
        {visible.map((a) => {
          const isTransfer = a.reason.toLowerCase().startsWith("traspaso");
          const positive = a.delta > 0;
          const neutral = a.delta === 0;
          return (
            <li key={a.id} className="px-4 py-3 flex items-start gap-3">
              <span
                className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${
                  isTransfer
                    ? "bg-accent-500/20 text-accent-200"
                    : "bg-secondary-500/20 text-secondary-100"
                }`}
              >
                <i className={isTransfer ? "ri-swap-box-line" : "ri-equalizer-line"} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-foreground-950 break-words">
                    {a.productName || "Producto"}
                  </p>
                  <span
                    className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                      neutral
                        ? "bg-background-300/40 text-foreground-600"
                        : positive
                        ? "bg-accent-500/20 text-accent-100"
                        : "bg-primary-500/20 text-primary-100"
                    }`}
                  >
                    {neutral ? "·" : `${positive ? "+" : "−"}${formatNumber(Math.abs(a.delta))} und`}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-600 mt-0.5 break-words">{a.reason}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">
                  {formatDate(a.date)} · Oficina {formatNumber(a.qtyBefore)} → {formatNumber(a.qtyAfter)}
                  {a.userName ? ` · ${a.userName}` : ""}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {adjustments.length > 8 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-4 py-2.5 text-[11px] font-semibold text-accent-200 hover:bg-background-200/40 cursor-pointer border-t border-background-300/30"
        >
          {expanded ? "Ver menos" : `Ver los ${adjustments.length} registros`}
        </button>
      )}
    </div>
  );
};

export default InventoryAdjustmentHistory;